import {
  AdminChipsResponse,
  AdminImpersonationStartRequest,
  AdminImpersonationStatusResponse,
  AdminLabWorkbooksResponse,
  AdminOrganisationShipmentsResponse,
  AdminPatchShipmentRequest,
  AdminShipmentResponse,
  AdminShipmentsResponse,
  AdminUserDirectoryResponse,
  AdminOrganisationAccessMetadataResponse,
  AdminOrganisationLabWorkbooksResponse,
  AdminSampleAccessResponse,
  AdminSampleChipsResponse,
  AdminSampleWithProjects,
  AdminSamplesResponse,
  AdminSamplingPointSamplesResponse,
  AdminSamplingPointsResponse,
  AdminSensorUploadResponse,
  AdminSensorsResponse,
  ChipReproducibilityRequest,
  ChipReproducibilityResponse,
  CreateSampleSheetRequest,
  CreateShipmentRequest,
  CreateShipmentResponse,
  DeleteSamplingPointProcessFlowResponse,
  DeleteLabWorkbookResponse,
  DeleteShipmentResponse,
  EnsureSampleSheetResponse,
  InviteLookupResponse,
  ImportLegacyLabSheetRequest,
  ImportLegacyLabSheetResponse,
  LabWorkbookSummaryResponse,
  LoginRequest,
  OrganisationMembersResponse,
  OrgSamplingPointChoicesResponse,
  PasswordResetFinalizeRequest,
  PasswordResetRequest,
  PatchLabWorkbookShippingSettingsRequest,
  PatchShippingNotificationSettingsRequest,
  PatchOrPostOrganisationRequest,
  PoolProjectRequest,
  PooledProjectCheckResponse,
  PostSamplingPointProcessFlowRequest,
  PostSamplingPointProcessFlowResponse,
  PostSamplingPointReverseProcessFlowResponse,
  PostOrganisationInvitesRequest,
  PostOrganisationInvitesResponse,
  PostOrganisationUserByEmailRequest,
  PostOrganisationUserByEmailResponse,
  PostOrganisationUserRequest,
  PostProjectRequest,
  ProjectImageRequest,
  QaLabelBatchRequest,
  QaLabelBatchResponse,
  QaLabelDeleteRequest,
  QaLabelDeleteResponse,
  QaLabelNoteRequest,
  QaLabelNoteResponse,
  QaLabelToggleResultStatusRequest,
  QaLabelToggleResultStatusResponse,
  QaLabelUndoRequest,
  QaLabelUndoResponse,
  SamplingPointWithProjectSamples,
  SignupInviteRequest,
  SignupUserRequest,
  ShippingWorkbooksResponse,
  ShippingNotificationSettingsResponse,
  ShipmentDocumentEmailRequest,
  ShipmentDocumentEmailResponse,
  ShipmentDocumentResponse,
  UpdateLabWorkbookMetadataRequest,
  VerifyUserRequest,
  WorldmapResponse,
  XAxisModePayload,
  zEnsureSampleSheetResponse,
  zLabWorkbookSummaryResponse,
  ProjectCtsResponse,
} from '@resistapp/common/api-types';
import {
  ChipDebugResponse,
  CountryByAlpha3,
  FullProject,
  Organisation,
  OrganisationSummary,
  OrganisationUserRole,
  PatchSampleRequest,
  PatchSamplingPointRequest,
  Project,
  RawUser,
  SamplingPoint,
  User,
  type ProjectSamplesUpdate,
  type SampleStatusUpdates,
} from '@resistapp/common/types';
import {
  QueryClient,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { useCallback, useEffect, useState } from 'react';
import useLocalStorageState from 'use-local-storage-state';
import { LocalStorageId } from '../components/shared/local-storage';
import { useSearchParamsContext } from '../contexts/search-params-context';
import { useUser } from '../contexts/use-user-context';
import { ApiError } from '../utils/error';
import { API_URL, getAuthHeaders, parseFetchResponseOld, resistomapApiClient } from '../utils/fetch';
import { canUseResearchHeatmapGeneSetSelector } from '../utils/research-heatmap-abundance-mode';
enum CallPhase {
  INITIAL = 0,
  LOADING = 1,
  SUCCESS = 2,
  ERROR = 3,
}

export interface ApiResponse<T> {
  data?: T | ApiError;
  status?: number;
  statusText?: string;
}

export interface CallInfo<T> {
  loading: boolean;
  error?: ApiError;
  data?: T;
}

interface DelayedCallInfo<R, T> extends CallInfo<T> {
  send: (body?: R) => Promise<ApiResponse<T>>;
}

function getCallInfo<T>(response: ApiResponse<T> | undefined, callStatus: CallPhase) {
  const status = response?.status;
  const loading = callStatus === CallPhase.LOADING;
  if (callStatus === CallPhase.INITIAL) {
    return { loading };
  } else if (callStatus === CallPhase.LOADING) {
    return { loading };
  } else if (callStatus === CallPhase.SUCCESS && !!status && status >= 200 && status <= 204) {
    return { loading, data: response.data as T };
  } else {
    const errorMaybe = response?.data as ApiError | undefined;
    const message = errorMaybe?.message || 'Oops, something went wrong';
    const error = new Error(message);
    (error as ApiError).statusCode = errorMaybe?.statusCode;
    error.name = errorMaybe?.name || error.name;
    return {
      loading,
      error,
    };
  }
}

export async function fetchOrThrow<RequestBody, Response>(
  method: string,
  path: string,
  bodyObj?: RequestBody,
): Promise<ApiResponse<Response>> {
  const url = `${API_URL}${path}`;
  const body: undefined | string | FormData =
    bodyObj && (bodyObj instanceof FormData ? (bodyObj as FormData) : JSON.stringify(bodyObj));
  const headers = getAuthHeaders(bodyObj instanceof FormData ? undefined : 'application/json');
  const response = await fetch(url, {
    method,
    headers,
    body,
    credentials: 'include',
  });

  return await parseFetchResponseOld(response);
}

function getFetchHandler<T>(
  promise: Promise<ApiResponse<T>> | undefined,
  setCallStatus: (value: React.SetStateAction<CallPhase>) => void,
  setResponse: (value: React.SetStateAction<ApiResponse<T> | undefined>) => void,
  cancelObj: { cancel: boolean },
) {
  return async () => {
    if (promise) {
      setCallStatus(CallPhase.LOADING);
      try {
        const result = await promise;
        if (!cancelObj.cancel) {
          setResponse(result);
          setCallStatus(CallPhase.SUCCESS);
        }
      } catch (error) {
        if (error instanceof Error) {
          if (!cancelObj.cancel) {
            setResponse({ data: new Error(error.message) });
            setCallStatus(CallPhase.ERROR);
          }
        } else {
          setResponse({ data: new Error('unknown error occured') });
          setCallStatus(CallPhase.ERROR);
        }
      }
    }
  };
}

export function useApiLater<R, T>(method: string, path: string): DelayedCallInfo<R, T> {
  const [callStatus, setCallStatus] = useState<CallPhase>(CallPhase.INITIAL);
  const [promise, setPromise] = useState<Promise<ApiResponse<T>> | undefined>(undefined);
  const [response, setResponse] = useState<ApiResponse<T> | undefined>(undefined);

  useEffect(() => {
    const cancelObj = { cancel: false };
    const handle = getFetchHandler(promise, setCallStatus, setResponse, cancelObj);
    void handle();
    return () => {
      cancelObj.cancel = true;
    };
  }, [promise]);

  const send = useCallback(
    async (body?: R) => {
      const p = fetchOrThrow<R, T>(method, path, body);
      setPromise(p);
      return await p;
    },
    [method, path],
  );

  return {
    ...getCallInfo(response, callStatus),
    send,
  };
}

export function useGet<D>(endpoint: string, options?: Omit<UseQueryOptions<D>, 'queryKey'>) {
  return useQuery<D>({
    queryKey: [endpoint],
    queryFn: async () => {
      const headers = getAuthHeaders('application/json');
      const response = await resistomapApiClient.get<D>(endpoint, { headers });
      return response.data;
    },
    ...(options || {}),
  });
}

function usePost<Request, Response>(
  endpoint: string,
  options?: UseMutationOptions<Response, Error, Request>,
): UseMutationResult<Response, Error, Request> {
  return useMutation<Response, Error, Request>({
    mutationFn: async (body: Request) => {
      const headers = getAuthHeaders('application/json');
      const response = await resistomapApiClient.post<Response>(endpoint, body, { headers });
      return response.data;
    },
    ...(options || {}),
  });
}

export function useDynamicPost<Request, Response, EndPointArgs>(
  getEndpoint: (args: EndPointArgs) => string,
  options?: Omit<UseMutationOptions<Response, Error, { body: Request; args: EndPointArgs }>, 'mutationFn'>,
): UseMutationResult<Response, Error, { body: Request; args: EndPointArgs }> {
  return useMutation<Response, Error, { body: Request; args: EndPointArgs }>({
    mutationFn: async ({ body, args }) => {
      const endpoint = getEndpoint(args);
      const headers = getAuthHeaders('application/json');
      const response = await resistomapApiClient.post<Response>(endpoint, body, { headers });
      return response.data;
    },
    ...(options || {}),
  });
}

function usePatch<Request, Response>(
  endpoint: string,
  options?: Omit<UseMutationOptions<Response, Error, Request>, 'mutationFn'>,
): UseMutationResult<Response, Error, Request> {
  return useMutation<Response, Error, Request>({
    mutationFn: async (body: Request) => {
      const headers = getAuthHeaders('application/json');
      const response = await resistomapApiClient.patch<Response>(endpoint, body, { headers });
      return response.data;
    },
    ...(options || {}),
  });
}

function useDelete<D>(endpoint: string) {
  return useMutation<D>({
    mutationFn: async () => {
      const headers = getAuthHeaders('application/json');
      const response = await resistomapApiClient.delete<D>(endpoint, { headers });
      return response.data;
    },
  });
}

export function useProjects(options?: Omit<UseQueryOptions<Project[]>, 'queryKey' | 'queryFn'>) {
  return useGet<Project[]>('/projects', options);
}

export function usePatchLabWorkbookShippingSettings(labWorkbookId: string) {
  return usePatch<PatchLabWorkbookShippingSettingsRequest, true>(
    `/admin/lab-workbooks/${labWorkbookId}/shipping-quota`,
  );
}

export function useShippingNotificationSettings() {
  return useGet<ShippingNotificationSettingsResponse>('/admin/shipping/notification-settings', {
    refetchOnWindowFocus: false,
  });
}

export function usePatchShippingNotificationSettings() {
  return usePatch<PatchShippingNotificationSettingsRequest, ShippingNotificationSettingsResponse>(
    '/admin/shipping/notification-settings',
  );
}

export type ApiGetter = <Response>(endpoint: string) => Promise<Response>;

export async function fetchOrganisationSummaries(get: ApiGetter): Promise<OrganisationSummary[]> {
  try {
    return await get<OrganisationSummary[]>('/organisation-summaries');
  } catch (error) {
    if (!isAxiosError(error) || error.response?.status !== 404) {
      throw error;
    }
    return await get<Organisation[]>('/organisations');
  }
}

export async function fetchOrganisationDetail(
  id: number,
  get: ApiGetter,
  getLegacyOrganisations: () => Promise<Organisation[]>,
): Promise<Organisation> {
  try {
    return await get<Organisation>(`/organisation/${id}`);
  } catch (error) {
    if (!isAxiosError(error) || error.response?.status !== 404) {
      throw error;
    }
    const organisation = (await getLegacyOrganisations()).find(candidate => candidate.id === id);
    if (!organisation) {
      throw error;
    }
    return organisation;
  }
}

async function authenticatedApiGet<Response>(endpoint: string): Promise<Response> {
  const headers = getAuthHeaders('application/json');
  const response = await resistomapApiClient.get<Response>(endpoint, { headers });
  return response.data;
}

const organisationSummariesQueryKey = ['/organisations'] as const;

export function useOrganisations(options?: Omit<UseQueryOptions<OrganisationSummary[]>, 'queryKey' | 'queryFn'>) {
  return useQuery<OrganisationSummary[]>({
    queryKey: organisationSummariesQueryKey,
    queryFn: async () => await fetchOrganisationSummaries(authenticatedApiGet),
    refetchOnWindowFocus: false,
    ...(options || {}),
  });
}

export function useOrganisation(id: number, options?: Omit<UseQueryOptions<Organisation>, 'queryKey' | 'queryFn'>) {
  const queryClient = useQueryClient();
  return useQuery<Organisation>({
    queryKey: [`/organisation/${id}`],
    queryFn: async () =>
      await fetchOrganisationDetail(id, authenticatedApiGet, async () => {
        const cachedOrganisations = queryClient.getQueryData<OrganisationSummary[]>(organisationSummariesQueryKey);
        const organisations =
          cachedOrganisations ??
          (await queryClient.fetchQuery<OrganisationSummary[]>({
            queryKey: organisationSummariesQueryKey,
            queryFn: async () => await fetchOrganisationSummaries(authenticatedApiGet),
          }));
        return organisations.filter(
          (organisation): organisation is Organisation =>
            'samplingPointAccesses' in organisation && 'sampleAccesses' in organisation,
        );
      }),
    refetchOnWindowFocus: false,
    ...(options || {}),
    enabled: Number.isFinite(id) && (options?.enabled ?? true),
  });
}

export function useEnsureOrganisationSampleSheet(
  organisationId: number | null | undefined,
  options?: { enabled?: boolean },
) {
  const enabled = Boolean(organisationId) && (options?.enabled ?? true);
  return useQuery<EnsureSampleSheetResponse>({
    queryKey: ['organisationSampleSheet', organisationId],
    enabled,
    staleTime: 30_000,
    retry: (failureCount, error) => {
      if (isAxiosError(error)) {
        const status = error.response?.status;
        const isClientError = typeof status === 'number' && status >= 400 && status < 500;
        if (isClientError && status !== 429) {
          return false;
        }
      }
      return failureCount < 3;
    },
    queryFn: async () => {
      if (!organisationId) {
        throw new Error('Organisation id is required');
      }
      const response = await resistomapApiClient.get<EnsureSampleSheetResponse>(
        `/admin/organisation/${organisationId}/sample-sheet`,
        { headers: getAuthHeaders('application/json') },
      );
      return zEnsureSampleSheetResponse.parse(response.data);
    },
  });
}

export function useCreateOrganisationSampleSheet(
  options?: Omit<
    UseMutationOptions<EnsureSampleSheetResponse, Error, CreateOrganisationSampleSheetVariables>,
    'mutationFn'
  >,
) {
  return useMutation<EnsureSampleSheetResponse, Error, CreateOrganisationSampleSheetVariables>({
    mutationFn: async ({ organisationId, body }) => {
      const headers = getAuthHeaders('application/json');
      const response = await resistomapApiClient.post<EnsureSampleSheetResponse>(
        `/admin/organisation/${organisationId}/sample-sheets`,
        body ?? {},
        { headers },
      );
      return zEnsureSampleSheetResponse.parse(response.data);
    },
    ...(options || {}),
  });
}

export function useOrganisationLabWorkbooks(
  organisationId: number | null | undefined,
  options?: Omit<UseQueryOptions<AdminOrganisationLabWorkbooksResponse>, 'queryKey' | 'queryFn'>,
) {
  const hasOrganisationId = typeof organisationId === 'number' && Number.isFinite(organisationId);
  const enabled = hasOrganisationId && (options?.enabled ?? true);
  return useQuery<AdminOrganisationLabWorkbooksResponse>({
    queryKey: ['organisationLabWorkbooks', organisationId],
    enabled,
    queryFn: async () => {
      if (!hasOrganisationId) {
        throw new Error('Organisation id is required');
      }
      const response = await resistomapApiClient.get<AdminOrganisationLabWorkbooksResponse>(
        `/admin/organisations/${organisationId}/lab-workbooks`,
        { headers: getAuthHeaders('application/json') },
      );
      return response.data;
    },
    ...(options || {}),
  });
}

export function useAdminOrganisationShipments(
  organisationId: number | null | undefined,
  params?: URLSearchParams | string,
  options?: Omit<UseQueryOptions<AdminOrganisationShipmentsResponse>, 'queryKey' | 'queryFn'>,
) {
  const hasOrganisationId = typeof organisationId === 'number' && Number.isFinite(organisationId);
  const qs = params ? (typeof params === 'string' ? params : params.toString()) : '';
  const endpoint = hasOrganisationId
    ? `/admin/organisations/${organisationId}/shipments${qs ? `?${qs}` : ''}`
    : '/admin/_skip';
  return useGet<AdminOrganisationShipmentsResponse>(endpoint, {
    enabled: hasOrganisationId && (options?.enabled ?? true),
    refetchOnWindowFocus: false,
    ...(options || {}),
  });
}

export function useCreateShipment() {
  return usePost<CreateShipmentRequest, CreateShipmentResponse>('/shipments');
}

export function useShippingWorkbooks(
  options?: Omit<UseQueryOptions<ShippingWorkbooksResponse>, 'queryKey' | 'queryFn'>,
) {
  return useGet<ShippingWorkbooksResponse>('/shipping/workbooks', {
    refetchOnWindowFocus: false,
    ...(options || {}),
  });
}

export function usePatchOrganisation() {
  return usePatch<PatchOrPostOrganisationRequest, Organisation>('/organisation');
}

export function useDeleteOrganisation(id: number) {
  return useDelete<null>(`/organisation/${id}`);
}

export function useCountries() {
  return useGet<CountryByAlpha3>('/countries', { refetchOnWindowFocus: false });
}

export function useWorldmap() {
  const { searchParams } = useSearchParamsContext();
  const all = searchParams.get('all');
  const type = searchParams.get('type');
  return useGet<WorldmapResponse>(`/worldmap?type=${type}${all ? '&all' : ''}`);
}

export function useProject(
  id: number | string,
  options?: Omit<UseQueryOptions<FullProject>, 'queryKey' | 'queryFn'>,
): ReturnType<typeof useQuery<FullProject>> {
  // Some old, incomplete projects may lack results
  const { searchParams } = useSearchParamsContext();
  const { user } = useUser();
  const [adminMode] = useLocalStorageState<boolean>(LocalStorageId.adminMode, {
    defaultValue: false,
  });
  const estimations =
    searchParams.get('estimations') === 'true' && canUseResearchHeatmapGeneSetSelector(user, adminMode);
  const query = estimations ? '?estimations=true' : '';
  return useGet<FullProject>(`/project/${id}${query}`, { refetchOnWindowFocus: false, ...(options || {}) });
}

export function buildProjectCtsEndpoint(projectId: number | string): string {
  return `/project/${encodeURIComponent(String(projectId))}/cts`;
}

// The Cq rows of a project are large and only needed for raw data downloads and admin tooltips, so they are
// loaded separately from the project and only when needed.
export function useProjectCts(
  projectId: number | undefined,
  options?: { enabled?: boolean },
): ReturnType<typeof useQuery<ProjectCtsResponse>> {
  return useGet<ProjectCtsResponse>(buildProjectCtsEndpoint(projectId ?? 0), {
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    enabled: projectId !== undefined && (options?.enabled ?? true),
  });
}

export async function fetchProjectCts(queryClient: QueryClient, projectId: number): Promise<ProjectCtsResponse> {
  const endpoint = buildProjectCtsEndpoint(projectId);
  return await queryClient.fetchQuery<ProjectCtsResponse>({
    queryKey: [endpoint],
    queryFn: async () => await authenticatedApiGet<ProjectCtsResponse>(endpoint),
    staleTime: Infinity,
  });
}

export function isProjectCtsQueryKey(queryKey: readonly unknown[]): boolean {
  return typeof queryKey[0] === 'string' && /^\/project\/[^/]+\/cts$/.test(queryKey[0]);
}

export function buildChipDebugEndpoint(chipId: number | string): string {
  return `/chip/${encodeURIComponent(String(chipId))}`;
}

// QA label changes alter the QA annotations of the Cq rows served for the chip and for every project the chip's
// samples belong to, so the cached project Cq rows are invalidated together with the chip.
export async function invalidateChipQaQueries(queryClient: QueryClient, chipId: number | string): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: [buildChipDebugEndpoint(chipId)] }),
    queryClient.invalidateQueries({ predicate: query => isProjectCtsQueryKey(query.queryKey) }),
  ]);
}

export function useChipDebug(chipId: number | string): ReturnType<typeof useQuery<ChipDebugResponse>> {
  return useGet<ChipDebugResponse>(buildChipDebugEndpoint(chipId));
}

export function useSignup() {
  const create = usePost<SignupUserRequest, boolean>(`/user/signup`);
  return { create };
}

export function useSignupInvite() {
  const create = usePost<SignupInviteRequest, User>(`/user/signup-invite`);
  return { create };
}

export function useInviteLookup(
  inviteCode: string | undefined,
  options?: Omit<UseQueryOptions<InviteLookupResponse>, 'queryKey' | 'queryFn'>,
) {
  return useGet<InviteLookupResponse>(`/invites/${inviteCode}`, {
    enabled: Boolean(inviteCode),
    ...(options || {}),
  });
}

export function useVerifyEmail() {
  const create = usePost<VerifyUserRequest, User>(`/user/verify`);
  return { create };
}

export function usePasswordResetRequest() {
  const create = usePost<PasswordResetRequest, null>(`/user/password-reset-request`);
  return { create };
}

export function usePasswordReset() {
  const create = usePost<PasswordResetFinalizeRequest, User>(`/user/password-reset`);
  return { create };
}

export function useProjectImage(id: number) {
  return usePost<ProjectImageRequest, Blob>(`/project/${id}/image`, {
    mutationFn: async (body: ProjectImageRequest) => {
      const response = await resistomapApiClient.post<Blob>(`/project/${id}/image`, body, {
        headers: {
          ...getAuthHeaders('application/json'),
          Accept: 'image/*',
        },
        responseType: 'blob',
      });
      return response.data;
    },
  });
}

export function useDeleteProject(id: number | string) {
  // Deep deletions are disabled (October 2025) but kept for reference:
  // const deepDelete = useApiLater<undefined, boolean>('DELETE', `/project/${id}?deep=true`);
  const shallowKeepDelete = useApiLater<undefined, boolean>('DELETE', `/project/${id}?deep=false&samplingAccess=keep`);
  const shallowRevokeDelete = useApiLater<undefined, boolean>(
    'DELETE',
    `/project/${id}?deep=false&samplingAccess=revoke`,
  );
  return {
    shallowKeep: shallowKeepDelete.send,
    shallowRevoke: shallowRevokeDelete.send,
    data: shallowKeepDelete.data || shallowRevokeDelete.data,
    error: shallowKeepDelete.error || shallowRevokeDelete.error,
  };
}

export function useDeleteAccess(projectId: number | string, userId: number | string) {
  return useApiLater<undefined, boolean>('DELETE', `/project/${projectId}/user/${userId}`);
}

export function useDeleteOrganisationUser(organisationId: number, userId: number) {
  return useDelete<boolean>(`/organisation/${organisationId}/user/${userId}`);
}

export function useDeleteOrganisationInvite(organisationId: number, inviteId: number) {
  return useDelete<boolean>(`/organisation/${organisationId}/invites/${inviteId}`);
}

export function usePostOrganisationInvites(organisationId: number) {
  return usePost<PostOrganisationInvitesRequest, PostOrganisationInvitesResponse>(
    `/organisation/${organisationId}/invites`,
  );
}

export function useOrganisationMembers(
  organisationId: number,
  options?: Omit<UseQueryOptions<OrganisationMembersResponse>, 'queryKey' | 'queryFn'>,
) {
  return useGet<OrganisationMembersResponse>(`/organisation/${organisationId}/members`, options);
}

export function usePostOrganisationUserByEmail(organisationId: number) {
  return usePost<PostOrganisationUserByEmailRequest, PostOrganisationUserByEmailResponse>(
    `/organisation/${organisationId}/user-by-email`,
  );
}

interface PostOrganisationUserArgs {
  organisationId: number;
  userId: number;
}
export function usePostOrganisationUser() {
  const poster = useDynamicPost<PostOrganisationUserRequest, boolean, PostOrganisationUserArgs>(
    ({ organisationId, userId }: PostOrganisationUserArgs) => `/organisation/${organisationId}/user/${userId}`,
  );
  return async (organisationId: number, userId: number, role: OrganisationUserRole = OrganisationUserRole.MEMBER) => {
    await poster.mutateAsync({ body: { role }, args: { organisationId, userId } });
  };
}
export function useUsers() {
  return useApiLater<undefined, User[]>('GET', `/users`);
}

export function useAdminUserDirectory() {
  return useApiLater<undefined, AdminUserDirectoryResponse>('GET', `/admin/user-directory`);
}

export function useAdminImpersonationStatus(options?: { enabled?: boolean }) {
  return useQuery<AdminImpersonationStatusResponse>({
    queryKey: ['adminImpersonationStatus'],
    enabled: options?.enabled ?? true,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      try {
        const headers = getAuthHeaders('application/json');
        const response = await resistomapApiClient.get<AdminImpersonationStatusResponse>(
          '/admin/impersonation/status',
          {
            headers,
          },
        );
        return response.data;
      } catch (error) {
        if (isAxiosError(error)) {
          const status = error.response?.status;
          if (status === 401 || status === 403) {
            return {
              active: false,
              realUser: null,
              effectiveUser: null,
              targetOrganisation: null,
            };
          }
        }
        throw error;
      }
    },
  });
}

export function useAdminStartImpersonation(
  options?: Omit<
    UseMutationOptions<AdminImpersonationStatusResponse, unknown, AdminImpersonationStartRequest>,
    'mutationFn'
  >,
) {
  return useMutation<AdminImpersonationStatusResponse, unknown, AdminImpersonationStartRequest>({
    mutationFn: async body => {
      const headers = getAuthHeaders('application/json');
      const response = await resistomapApiClient.post<AdminImpersonationStatusResponse>(
        '/admin/impersonation/start',
        body,
        {
          headers,
        },
      );
      return response.data;
    },
    ...(options || {}),
  });
}

export function useAdminStopImpersonation(
  options?: Omit<UseMutationOptions<AdminImpersonationStatusResponse, unknown, undefined>, 'mutationFn'>,
) {
  return useMutation<AdminImpersonationStatusResponse, unknown, undefined>({
    mutationFn: async () => {
      const headers = getAuthHeaders('application/json');
      const response = await resistomapApiClient.post<AdminImpersonationStatusResponse>(
        '/admin/impersonation/stop',
        {},
        { headers },
      );
      return response.data;
    },
    ...(options || {}),
  });
}

export function useDeleteUser(id: number | string) {
  return useApiLater<undefined, boolean>('DELETE', `/user/${id}`);
}

export function useDeleteUserProject(userId: number | string, projectId: number | string) {
  return useApiLater<undefined, boolean>('DELETE', `/user/${userId}/project/${projectId}`);
}
export function useDeleteOrganisationProject(organisationId: number | string, projectId: number | string) {
  const keep = useApiLater<undefined, boolean>(
    'DELETE',
    `/organisation/${organisationId}/project/${projectId}?samplingAccess=keep`,
  );
  const revoke = useApiLater<undefined, boolean>(
    'DELETE',
    `/organisation/${organisationId}/project/${projectId}?samplingAccess=revoke`,
  );
  return {
    keep: keep.send,
    revoke: revoke.send,
    data: keep.data || revoke.data,
    error: keep.error || revoke.error,
  };
}

export function useGetUser() {
  return useApiLater<undefined, User>('GET', '/user');
}

export function usePostLogin(onSuccess?: (data: User) => void, onError?: (error: Error) => void) {
  const poster = useDynamicPost<LoginRequest, User, undefined>(() => `/login`, { onSuccess, onError });
  return async (email: string, password: string) => {
    await poster.mutateAsync({ body: { email, password }, args: undefined });
  };
}

export function usePostProject() {
  const { loading, error, data, send: sendData } = useApiLater<FormData, FullProject>('POST', `/project`);
  const send = (body: PostProjectRequest) => {
    const formData = new FormData();
    formData.append('name', body.name);
    if (body.sheetLink) {
      formData.append('sheetLink', body.sheetLink);
    }
    void sendData(formData);
  };
  return { loading, error, data, send };
}

export function useImportLegacyLabSheet() {
  const {
    loading,
    error,
    data,
    send: sendData,
  } = useApiLater<ImportLegacyLabSheetRequest, ImportLegacyLabSheetResponse>(
    'POST',
    `/lab-workbooks/import-legacy-labsheet`,
  );
  const send = (body: ImportLegacyLabSheetRequest) => {
    void sendData(body);
  };
  return { loading, error, data, send };
}

export function useResetLabWorkbook() {
  return useMutation<LabWorkbookSummaryResponse, Error, { workbookId: string }>({
    mutationFn: async ({ workbookId }) => {
      const headers = getAuthHeaders('application/json');
      const response = await resistomapApiClient.post<LabWorkbookSummaryResponse>(
        `/lab/${workbookId}/reset`,
        undefined,
        { headers },
      );
      return response.data;
    },
  });
}

export function useDeleteLabWorkbook() {
  return useMutation<DeleteLabWorkbookResponse, Error, { workbookId: string }>({
    mutationFn: async ({ workbookId }) => {
      const headers = getAuthHeaders('application/json');
      const response = await resistomapApiClient.post<DeleteLabWorkbookResponse>(
        `/lab/${workbookId}/delete`,
        undefined,
        {
          headers,
        },
      );
      return response.data;
    },
  });
}

export function useUpdateLabWorkbookMetadata() {
  return useMutation<LabWorkbookSummaryResponse, Error, { workbookId: string; body: UpdateLabWorkbookMetadataRequest }>(
    {
      mutationFn: async ({ workbookId, body }) => {
        const headers = getAuthHeaders('application/json');
        const response = await resistomapApiClient.patch<LabWorkbookSummaryResponse>(
          `/lab/${workbookId}/metadata`,
          body,
          {
            headers,
          },
        );
        return zLabWorkbookSummaryResponse.parse(response.data);
      },
    },
  );
}

export function usePostPooledProject() {
  const check = usePost<PoolProjectRequest, PooledProjectCheckResponse>(`/pooled-project/check`);
  const create = usePost<PoolProjectRequest, FullProject>(`/pooled-project/create`);
  return {
    check,
    create,
  };
}

export function usePatchProjectImages(id: number) {
  const { loading, error, data, send: sendData } = useApiLater<FormData, true>('PATCH', `/project/${id}/filenames`);
  const send = (files: File[], deleteFilename?: string) => {
    const formData = new FormData();
    files.forEach((file, idx) => {
      formData.append(`file${idx}`, file);
    });
    if (deleteFilename) {
      formData.append(`deleteFilename`, deleteFilename);
    }
    void sendData(formData);
  };
  return { loading, error, data, send };
}

export function usePatchProjectName(id: number, onSuccess?: (data: true) => void) {
  return usePatch<string, true>(`/project/${id}/name`, { onSuccess });
}

// NOT USED ANYMORE. Maybe in the future
export function usePostUser(): UseMutationResult<User, Error, RawUser> {
  return usePost<RawUser, User>(`/user`);
}

export function usePatchProjectLevels(id: number) {
  return usePatch<Record<string, number[]>, true>(`/project/${id}/zoomable-levels`);
}

export function usePatchSampleStatuses(onSuccess?: (data: true) => void, onError?: (error: Error) => void) {
  return usePatch<SampleStatusUpdates, true>(`/samples/statuses`, { onSuccess, onError });
}

export function usePatchProjectSamples(onSuccess?: (data: true) => void, onError?: (error: Error) => void) {
  return usePatch<ProjectSamplesUpdate, true>(`/project/samples`, { onSuccess, onError });
}

export function usePatchProjectXAxisMode(id: number, onSuccess?: (data: true) => void) {
  return usePatch<XAxisModePayload, true>(`/project/${id}/x-axis-mode`, { onSuccess });
}

export function useCreateQaLabelBatch() {
  return usePost<QaLabelBatchRequest, QaLabelBatchResponse>(`/qa-label/batch`);
}

export function useDeleteQaLabels() {
  return usePost<QaLabelDeleteRequest, QaLabelDeleteResponse>(`/qa-label/delete`);
}

export function useUpdateQaLabelNotes() {
  return usePost<QaLabelNoteRequest, QaLabelNoteResponse>(`/qa-label/note`);
}

export function useToggleQaLabelResultStatus() {
  return usePost<QaLabelToggleResultStatusRequest, QaLabelToggleResultStatusResponse>(`/qa-label/toggle-result-status`);
}

export function useUndoQaLabelBatch() {
  return usePost<QaLabelUndoRequest, QaLabelUndoResponse>(`/qa-label/undo`);
}

interface AffectedProject {
  id: number;
  name: string;
  sampleCount: number;
}

export interface SamplingPointCombineCheckResult {
  affectedProjects: AffectedProject[];
  canCombine: boolean;
  message?: string;
}

export interface SamplingPointCombineParams {
  name: string;
  type: string;
  subtype: string | null;
  organisationId?: number | null;
}

export function useCheckSamplingPointCombine() {
  return usePost<SamplingPointCombineParams, SamplingPointCombineCheckResult>('/sampling-point/check');
}

export function useCombineSamplingPoints() {
  return usePost<SamplingPointCombineParams, SamplingPointCombineCheckResult>('/sampling-point/combine');
}

export function usePostChipReproducibility() {
  return usePost<ChipReproducibilityRequest, ChipReproducibilityResponse>('/chip/reproducibility');
}

// Admin browsing APIs
export function useAdminSamples(params: URLSearchParams | string) {
  const qs = typeof params === 'string' ? params : params.toString();
  return useGet<AdminSamplesResponse>(`/admin/samples${qs ? `?${qs}` : ''}`);
}

type CreateOrganisationSampleSheetVariables = {
  organisationId: number;
  body?: Partial<CreateSampleSheetRequest>;
};

export function useAdminSampleChips(sampleId: number) {
  return useGet<AdminSampleChipsResponse>(`/admin/samples/${sampleId}/chips`, { refetchOnWindowFocus: false });
}

export function useAdminSampleAccess(sampleId: number | undefined) {
  const enabled = typeof sampleId === 'number' && Number.isFinite(sampleId);
  const endpoint = enabled ? `/admin/samples/${sampleId}/access` : '/admin/_skip';
  return useGet<AdminSampleAccessResponse>(endpoint, { refetchOnWindowFocus: false, enabled });
}

export function useOrgSamplingPointChoices(organisationId: number | undefined) {
  const hasOrganisation = typeof organisationId === 'number' && Number.isFinite(organisationId);
  const endpoint = hasOrganisation ? `/admin/org-sampling-point-choices/${organisationId}` : '/admin/_skip';
  return useGet<OrgSamplingPointChoicesResponse>(endpoint, {
    enabled: hasOrganisation,
    refetchOnWindowFocus: false,
  });
}

export function useAdminSamplingPointAccess(samplingPointId: number | undefined) {
  const enabled = typeof samplingPointId === 'number' && Number.isFinite(samplingPointId);
  const endpoint = enabled ? `/admin/sampling-points/${samplingPointId}/access` : '/admin/_skip';
  return useGet<AdminSampleAccessResponse>(endpoint, { refetchOnWindowFocus: false, enabled });
}

export function useAdminOrganisationAccessMetadata(
  organisationId: number | undefined,
  options?: { enabled?: boolean },
) {
  const hasValidId = typeof organisationId === 'number' && Number.isFinite(organisationId);
  const enabled = (options?.enabled ?? true) && hasValidId;
  const endpoint = enabled ? `/admin/organisations/${organisationId}/access-metadata` : '/admin/_skip';
  return useGet<AdminOrganisationAccessMetadataResponse>(endpoint, { enabled, refetchOnWindowFocus: false });
}

export function useAdminSamplingPoints(params: URLSearchParams | string) {
  const qs = typeof params === 'string' ? params : params.toString();
  return useGet<AdminSamplingPointsResponse>(`/admin/sampling-points${qs ? `?${qs}` : ''}`);
}

export function useAdminSamplingPointSamples(samplingPointId: number) {
  return useGet<AdminSamplingPointSamplesResponse>(`/admin/sampling-points/${samplingPointId}/samples`, {
    refetchOnWindowFocus: false,
  });
}

export function useAdminChips(params: URLSearchParams | string) {
  const qs = typeof params === 'string' ? params : params.toString();
  return useGet<AdminChipsResponse>(`/admin/chips${qs ? `?${qs}` : ''}`);
}

export function useAdminLabWorkbooks(params: URLSearchParams | string) {
  const qs = typeof params === 'string' ? params : params.toString();
  return useGet<AdminLabWorkbooksResponse>(`/admin/lab-workbooks${qs ? `?${qs}` : ''}`, {
    refetchOnWindowFocus: false,
  });
}

export function useAdminSensors() {
  return useGet<AdminSensorsResponse>('/admin/sensors', {
    refetchOnWindowFocus: false,
    refetchInterval: 10_000,
  });
}

export function usePostAdminSensorsUpload() {
  return useMutation<AdminSensorUploadResponse, Error, FormData>({
    mutationFn: async body => {
      const headers = getAuthHeaders();
      const response = await resistomapApiClient.post<AdminSensorUploadResponse>('/admin/sensors/upload', body, {
        headers,
      });
      return response.data;
    },
  });
}

export function useAdminShipments(
  params: URLSearchParams | string,
  options?: Omit<UseQueryOptions<AdminShipmentsResponse>, 'queryKey' | 'queryFn'>,
) {
  const qs = typeof params === 'string' ? params : params.toString();
  return useGet<AdminShipmentsResponse>(`/admin/shipments${qs ? `?${qs}` : ''}`, {
    refetchOnWindowFocus: false,
    ...(options || {}),
  });
}

export function useShipments(
  params: URLSearchParams | string,
  options?: Omit<UseQueryOptions<AdminShipmentsResponse>, 'queryKey' | 'queryFn'>,
) {
  const qs = typeof params === 'string' ? params : params.toString();
  return useGet<AdminShipmentsResponse>(`/shipments${qs ? `?${qs}` : ''}`, {
    refetchOnWindowFocus: false,
    ...(options || {}),
  });
}

export function useCancelShipment() {
  return useMutation<AdminShipmentResponse, Error, { shipmentId: number }>({
    mutationFn: async ({ shipmentId }) => {
      const headers = getAuthHeaders('application/json');
      const response = await resistomapApiClient.patch<AdminShipmentResponse>(
        `/shipments/${shipmentId}/cancel`,
        {},
        { headers },
      );
      return response.data;
    },
  });
}

export function useAdminShipment(shipmentId: number | undefined) {
  const enabled = typeof shipmentId === 'number' && Number.isFinite(shipmentId);
  const endpoint = enabled ? `/admin/shipment/${shipmentId}` : '/admin/_skip';
  return useGet<AdminShipmentResponse>(endpoint, {
    enabled,
    refetchOnWindowFocus: false,
  });
}

export function usePatchAdminShipment() {
  return useMutation<AdminShipmentResponse, Error, { shipmentId: number; body: AdminPatchShipmentRequest }>({
    mutationFn: async ({ shipmentId, body }) => {
      const headers = getAuthHeaders('application/json');
      const response = await resistomapApiClient.patch<AdminShipmentResponse>(`/admin/shipment/${shipmentId}`, body, {
        headers,
      });
      return response.data;
    },
  });
}

export function useDeleteAdminShipment() {
  return useMutation<DeleteShipmentResponse, Error, { shipmentId: number }>({
    mutationFn: async ({ shipmentId }) => {
      const headers = getAuthHeaders('application/json');
      const response = await resistomapApiClient.delete<DeleteShipmentResponse>(`/admin/shipment/${shipmentId}`, {
        headers,
      });
      return response.data;
    },
  });
}

export function useUploadAdminShipmentDocument() {
  return useMutation<ShipmentDocumentResponse, Error, { shipmentId: number; body: FormData }>({
    mutationFn: async ({ shipmentId, body }) => {
      const response = await resistomapApiClient.post<ShipmentDocumentResponse>(
        `/admin/shipment/${shipmentId}/documents`,
        body,
        { headers: getAuthHeaders() },
      );
      return response.data;
    },
  });
}

export function useDeleteAdminShipmentDocument() {
  return useMutation<true, Error, { shipmentId: number; documentId: number }>({
    mutationFn: async ({ shipmentId, documentId }) => {
      const response = await resistomapApiClient.delete<true>(`/admin/shipment/${shipmentId}/documents/${documentId}`, {
        headers: getAuthHeaders('application/json'),
      });
      return response.data;
    },
  });
}

export function useDownloadShipmentDocument() {
  return useMutation<Blob, Error, { shipmentId: number; documentId: number }>({
    mutationFn: async ({ shipmentId, documentId }) => {
      const response = await resistomapApiClient.get<Blob>(`/shipments/${shipmentId}/documents/${documentId}`, {
        headers: getAuthHeaders(),
        responseType: 'blob',
      });
      return response.data;
    },
  });
}

export function useSendAdminShipmentDocumentEmail() {
  return useMutation<ShipmentDocumentEmailResponse, Error, { shipmentId: number; body: ShipmentDocumentEmailRequest }>({
    mutationFn: async ({ shipmentId, body }) => {
      const response = await resistomapApiClient.post<ShipmentDocumentEmailResponse>(
        `/admin/shipment/${shipmentId}/document-email`,
        body,
        { headers: getAuthHeaders('application/json') },
      );
      return response.data;
    },
  });
}

// Sampling point CRUD APIs
export function useSamplingPoint(samplingPointId: number | undefined) {
  const enabled = typeof samplingPointId === 'number' && !Number.isNaN(samplingPointId);
  const endpoint = enabled ? `/sampling-point/${samplingPointId}` : '/sampling-point/_skip';
  return useGet<SamplingPointWithProjectSamples>(endpoint, { refetchOnWindowFocus: false, enabled });
}

export function usePatchSamplingPoint() {
  return usePatch<PatchSamplingPointRequest, SamplingPoint>('/sampling-point');
}

export function usePostSamplingPointProcessFlow() {
  return useDynamicPost<
    PostSamplingPointProcessFlowRequest,
    PostSamplingPointProcessFlowResponse,
    { samplingPointId: number }
  >(args => `/sampling-point/${args.samplingPointId}/process-flows`);
}

export function useDeleteSamplingPointProcessFlow(
  options?: Omit<
    UseMutationOptions<
      DeleteSamplingPointProcessFlowResponse,
      Error,
      { samplingPointId: number; processFlowId: number }
    >,
    'mutationFn'
  >,
) {
  return useMutation<DeleteSamplingPointProcessFlowResponse, Error, { samplingPointId: number; processFlowId: number }>(
    {
      mutationFn: async ({ samplingPointId, processFlowId }) => {
        const headers = getAuthHeaders('application/json');
        const response = await resistomapApiClient.delete<DeleteSamplingPointProcessFlowResponse>(
          `/sampling-point/${samplingPointId}/process-flows/${processFlowId}`,
          { headers },
        );
        return response.data;
      },
      ...(options || {}),
    },
  );
}

export function usePostSamplingPointReverseProcessFlow(
  options?: Omit<
    UseMutationOptions<
      PostSamplingPointReverseProcessFlowResponse,
      Error,
      { samplingPointId: number; processFlowId: number }
    >,
    'mutationFn'
  >,
) {
  return useMutation<
    PostSamplingPointReverseProcessFlowResponse,
    Error,
    { samplingPointId: number; processFlowId: number }
  >({
    mutationFn: async ({ samplingPointId, processFlowId }) => {
      const headers = getAuthHeaders('application/json');
      const response = await resistomapApiClient.post<PostSamplingPointReverseProcessFlowResponse>(
        `/sampling-point/${samplingPointId}/process-flows/${processFlowId}/reverse`,
        {},
        { headers },
      );
      return response.data;
    },
    ...(options || {}),
  });
}

// Sample admin APIs
export function useAdminSample(sampleId: number | undefined) {
  const enabled = typeof sampleId === 'number' && !Number.isNaN(sampleId);
  const endpoint = enabled ? `/admin/sample/${sampleId}` : '/admin/_skip';
  return useGet<AdminSampleWithProjects>(endpoint, { refetchOnWindowFocus: false, enabled });
}

export function usePatchSample() {
  return usePatch<PatchSampleRequest, AdminSampleWithProjects>('/sample');
}
